import { Add, Delete, History, Person, Warning } from '@mui/icons-material';
import {
  Chip,
  useMediaQuery,
  useTheme,
  ListItem,
  ListItemText,
  MenuItem,
  ListItemIcon,
  Typography,
  Stack,
} from '@mui/material';
import MobilePanelList from 'components/lists/MobilePanelList';
import { TAB_LAYOUT_HEADER_HEIGHT } from 'components/layout/FormTabNavigation';
import { AppContext } from 'context/appContext';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Outlet,
  useFetcher,
  useLocation,
  useNavigate,
  useOutletContext,
  useRouteLoaderData,
} from 'react-router';
import { ROUTES, routeWithParams } from 'router';
import {
  Contact,
  EntityTypes,
  Property,
  PropertyGroup,
  Status,
  Tenant,
  TraxeroActionResponse,
  FormMode,
  DisplayMode,
  Column,
} from 'types';
import {
  capitalize,
  formatPhoneNumber,
  getShortFormattedAddress,
  getBlankContact,
  logError,
} from 'utils';
import AuthService from 'utils/AuthService';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  MRT_Row,
  MRT_SortingState,
  MRT_RowVirtualizer,
} from 'material-react-table';
import EntityLookup from 'components/autocompletes/EntityLookup';
import {
  TableType,
  useDefaultTableOptions,
} from 'hooks/useDefaultTableOptions';
import MRTableToolbar from 'components/tables/PanelTableToolbar';
import { ListItemMenuButton } from 'components/buttons/ListItemMenuButton';
import { useTranslation } from 'react-i18next';
import { useFetcherKey } from 'hooks/useFetcherKey';
import FormFrame from 'components/layout/FormFrame';
import {
  getActionRouteForDisplayMode,
  getLoaderRouteForDisplayMode,
  PeopleTabActionHandler,
  PeopleTabLayoutLoaderData,
  PeopleTabLayoutContext,
} from './PeopleTabLayout';

export interface ContactContext {
  setContact: (contact: Contact) => void;
  contact: Contact;
  contacts: Contact[];
  onBack: () => void;
  formMode: FormMode;
  displayMode: DisplayMode;
  fetcherKey: string;
  entity: Property | Tenant | PropertyGroup;
}

export const ContactsPanel = (): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const theme = useTheme();
  const { breakpoints } = theme;
  const { pathname } = useLocation();
  const displayToast = useContext(ToastContext);
  const mobile = useMediaQuery(breakpoints.down('lg'));
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const navigate = useNavigate();
  const { formMode, displayMode, setFormMode, entity } =
    useOutletContext<PeopleTabLayoutContext>();
  const { _contacts } = useRouteLoaderData(
    getLoaderRouteForDisplayMode(displayMode),
  ) as PeopleTabLayoutLoaderData;

  const [contacts, setContacts] = useState<Contact[]>(entity?.contacts || []);
  const [contact, setContact] = useState<Contact>(
    getBlankContact(state.tenant.id),
  );

  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);
  const canCreate = state.entityPermissions[EntityTypes.Contact].create;
  const canAddRemove = useMemo(() => {
    if (AuthService.IsSuperAdmin() && displayMode !== 'tenant') {
      return true;
    }
    if (displayMode === 'property') {
      return state.entityPermissions.property.update;
    }
    if (displayMode === 'group') {
      return state.entityPermissions.property_group.update;
    }
    return false;
  }, [displayMode]);

  // await data fetch
  useEffect(() => {
    const awaitLoaderData = async () => {
      const c = await _contacts;
      setContacts(c);
      setIsLoading(false);
    };
    awaitLoaderData();
  }, [_contacts]);

  useEffect(() => {
    if (formMode === 'create') {
      setContact(getBlankContact(state.tenant.id));
    }
  }, [formMode]);

  useEffect(() => {
    //scroll to the top of the table when the sorting changes
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
    } catch (error) {
      if (error instanceof Error) {
        logError(error);
      } else {
        logError(new Error(String(error)));
      }
    }
  }, [sorting]);

  // handle action responses
  const getSuccessMessage = useCallback(
    (res: TraxeroActionResponse<PeopleTabActionHandler>): string => {
      switch (res.handler) {
        case PeopleTabActionHandler.TENANT_CONTACT_CREATE:
        case PeopleTabActionHandler.PROPERTY_CONTACT_CREATE:
        case PeopleTabActionHandler.PROPERTY_GROUP_CONTACT_CREATE:
          return t('create_success', {
            name: `${t('contact.label', { ns: 'entities' })} ${contact?.name}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.TENANT_CONTACT_UPDATE:
        case PeopleTabActionHandler.PROPERTY_CONTACT_UPDATE:
        case PeopleTabActionHandler.PROPERTY_GROUP_CONTACT_UPDATE:
          return t('update_success', {
            name: `${t('contact.label', { ns: 'entities' })} ${contact?.name}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.PROPERTY_CONTACT_UPSERT:
          return t('update_success', {
            name: `${t('property.label', { ns: 'entities' })}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.PROPERTY_GROUP_CONTACT_UPSERT:
          return t('update_success', {
            name: `${t('property_group.label', { ns: 'entities' })}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.CONTACT_DELETE:
          return t('delete_success', {
            name: `${t('contact.label', { ns: 'entities' })} ${
              res.body?.name || ''
            }`,
            ns: 'toasts',
          });
        default:
          return '';
      }
    },
    [contact?.name, t],
  );
  const getErrorMessage = useCallback(
    (res: TraxeroActionResponse<PeopleTabActionHandler>): string => {
      const { handler, status, statusText } = res;
      switch (handler) {
        case PeopleTabActionHandler.TENANT_CONTACT_UPDATE:
        case PeopleTabActionHandler.PROPERTY_CONTACT_UPDATE:
        case PeopleTabActionHandler.PROPERTY_GROUP_CONTACT_UPDATE:
          return t('update_error', {
            name: `${t('contact.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.PROPERTY_CONTACT_CREATE:
        case PeopleTabActionHandler.PROPERTY_GROUP_CONTACT_CREATE:
        case PeopleTabActionHandler.TENANT_CONTACT_CREATE:
          return t('create_error', {
            name: `${t('contact.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.PROPERTY_CONTACT_UPSERT:
          return t('update_error', {
            name: `${t('property.label', { ns: 'entities' })}${t(
              'contact.label_plural',
              { ns: 'entities' },
            )}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.PROPERTY_GROUP_CONTACT_UPSERT:
          return t('update_error', {
            name: `${t('property_group.label', { ns: 'entities' })}${t(
              'contact.label_plural',
              { ns: 'entities' },
            )}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.CONTACT_DELETE:
          return t('delete_error', {
            name: `${t('contact.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        default:
          return t('unknown_error', { ns: 'toasts' });
      }
    },
    [],
  );
  const handleNavigation = useCallback(
    (handler: PeopleTabActionHandler) => {
      switch (handler) {
        case PeopleTabActionHandler.TENANT_CONTACT_CREATE:
        case PeopleTabActionHandler.TENANT_CONTACT_UPDATE:
          navigate(routeWithParams(ROUTES.CONTACTS, [state.tenant.id]));
          break;
        case PeopleTabActionHandler.PROPERTY_CONTACT_UPSERT:
        case PeopleTabActionHandler.PROPERTY_CONTACT_CREATE:
        case PeopleTabActionHandler.PROPERTY_CONTACT_UPDATE:
          navigate(
            routeWithParams(ROUTES.PROPERTY_CONTACTS, [
              state.tenant.id,
              entity.id,
            ]),
          );
          break;
        case PeopleTabActionHandler.PROPERTY_GROUP_CONTACT_UPSERT:
        case PeopleTabActionHandler.PROPERTY_GROUP_CONTACT_CREATE:
        case PeopleTabActionHandler.PROPERTY_GROUP_CONTACT_UPDATE:
          navigate(
            routeWithParams(ROUTES.PROPERTY_GROUP_CONTACTS, [
              state.tenant.id,
              entity.id,
            ]),
          );
          break;
      }
    },
    [navigate, state.tenant.id, entity.id],
  );

  useEffect(() => {
    if (fetcher.data) {
      const response =
        fetcher.data as TraxeroActionResponse<PeopleTabActionHandler>;
      if (response.status < 400) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: getSuccessMessage(response),
        });
        setFormMode('none');
        setContact(getBlankContact(state.tenant.id));
        handleNavigation(getHandlerForDisplayMode(displayMode, formMode));
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: getErrorMessage(response),
        });
      }
    }
  }, [
    fetcher.data,
    displayMode,
    formMode,
    handleNavigation,
    getSuccessMessage,
    getErrorMessage,
    state.tenant.id,
  ]);

  const handleRemoveContact = (id: number) => {
    const updatedContacts: Contact[] = contacts.filter((c) => c.id !== id);
    fetcher.submit(
      {
        handler: getHandlerForDisplayMode(displayMode, formMode),
        body: JSON.stringify(updatedContacts),
        tenantID: state.tenant.id,
        entityID: entity.id,
        contactID: id,
      },
      {
        method: 'PUT',
        action: getActionRouteForDisplayMode({
          displayMode,
          propertyID: entity.id,
          groupID: entity.id,
          tenantID: state.tenant.id,
        }),
        encType: 'application/json',
      },
    );
  };
  const handleSoftDelete = (contact: Contact): void => {
    const updatedContact = { ...contact, status: Status.Disabled };
    fetcher.submit(
      {
        handler: PeopleTabActionHandler.CONTACT_DELETE,
        body: JSON.stringify(updatedContact),
        tenantID: state.tenant.id,
        contactID: contact.id,
      },
      {
        method: 'PUT',
        action: getActionRouteForDisplayMode({
          displayMode,
          propertyID: entity.id,
          groupID: entity.id,
          tenantID: state.tenant.id,
        }),
        encType: 'application/json',
      },
    );
  };
  const handleMakePrimary = (id: number) => {
    const otherContacts: Contact[] = contacts.filter((c) => c.id !== id);
    const primaryContact: Contact | undefined = contacts.find(
      (c) => c.id === id,
    );
    if (primaryContact) {
      fetcher.submit(
        {
          handler: getHandlerForDisplayMode(displayMode, formMode),
          body: JSON.stringify([primaryContact, ...otherContacts]),
          tenantID: state.tenant.id,
          entityID: entity.id,
          contactID: primaryContact.id,
        },
        {
          method: 'PUT',
          action: getActionRouteForDisplayMode({
            displayMode,
            propertyID: entity.id,
            groupID: entity.id,
            tenantID: state.tenant.id,
          }),
          encType: 'application/json',
        },
      );
    }
  };
  const handleContactClick = (id: number) => {
    const selected: Contact | undefined = contacts.find((c) => c.id === id);
    if (selected) {
      setFormMode('edit');
      setContact(selected);
    } else {
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t('find_error', {
          name: t('contact.label', { ns: 'entities' }),
          ns: 'toasts',
        }),
      });
      return;
    }

    if (displayMode === 'tenant') {
      navigate(routeWithParams(ROUTES.CONTACTS_FORM, [state.tenant.id, id]));
    } else {
      navigate(
        routeWithParams(getFormRouteForDisplayMode(displayMode), [
          state.tenant.id,
          entity?.id,
          id,
        ]),
      );
    }
  };
  const handleAddContact = useCallback(
    (c: Contact | null) => {
      if (c) {
        const contactsArr = [...contacts, c];
        fetcher.submit(
          {
            handler: getHandlerForDisplayMode(displayMode, formMode),
            body: JSON.stringify(contactsArr),
            tenantID: state.tenant.id,
            entityID: entity.id,
            contactID: c.id,
          },
          {
            method: 'PUT',
            action: getActionRouteForDisplayMode({
              displayMode,
              propertyID: entity.id,
              groupID: entity.id,
              tenantID: state.tenant.id,
            }),
            encType: 'application/json',
          },
        );
      }
    },
    [contact, contacts, displayMode],
  );
  const handleViewRevisions = useCallback(
    (id: number) => {
      navigate(
        routeWithParams(ROUTES.REVISIONS, [
          state.tenant.id,
          EntityTypes.Contact,
          id,
        ]),
      );
    },
    [state.tenant.id, navigate],
  );
  const handleCancelAction = () => {
    setContact(getBlankContact(state.tenant.id));
    handleNavigation(getHandlerForDisplayMode(displayMode, formMode));
    setFormMode('none');
  };

  const handleCreateClick = useCallback(() => {
    setContact(getBlankContact(state.tenant.id));
    setFormMode('create');
    navigate(`${pathname}/0`);
  }, [pathname, navigate, state.tenant.id]);

  const columns = useMemo<MRT_ColumnDef<Contact>[]>(
    () => [
      {
        header: t('contact.id', { ns: 'entities' }),
        accessorKey: 'id',
      },
      {
        header: t('contact.name', { ns: 'entities' }),
        accessorKey: 'name',
        Cell: ({
          row,
        }: {
          row: MRT_Row<Contact>;
          renderedCellValue: ReactNode;
        }) => {
          return (
            <React.Fragment>
              {row.original.name}
              {!row.index && displayMode !== 'tenant' && (
                <Chip
                  color={'primary'}
                  icon={<Person />}
                  label={t('primary')}
                  variant={'filled'}
                  size={'small'}
                  sx={{
                    fontWeight: 'bold',
                    px: 1,
                    ml: 2,
                  }}
                />
              )}
            </React.Fragment>
          );
        },
      },
      {
        header: t('contact.description', { ns: 'entities' }),
        accessorKey: 'description',
      },
      {
        header: t('contact.primaryEmail', { ns: 'entities' }),
        accessorKey: 'primaryEmail',
      },
      {
        header: t('contact.secondaryEmail', { ns: 'entities' }),
        accessorKey: 'secondaryEmail',
      },
      {
        header: t('contact.primaryPhone', { ns: 'entities' }),
        accessorKey: 'primaryPhone',
        accessorFn: (row) =>
          `${formatPhoneNumber(row.primaryPhone)} ${
            row?.primaryPhoneType ? `(${row.primaryPhoneType})` : ''
          }`,
      },
      {
        header: t('contact.secondaryPhone', { ns: 'entities' }),
        accessorKey: 'secondaryPhone',
        accessorFn: (row) =>
          `${formatPhoneNumber(row.secondaryPhone)} (${
            row.secondaryPhoneType
          })`,
      },
      {
        header: t('contact.tertiaryPhone', { ns: 'entities' }),
        accessorKey: 'tertiaryPhone',
        accessorFn: (row) =>
          `${formatPhoneNumber(row.tertiaryPhone)} (${row.tertiaryPhoneType})`,
      },
      {
        header: t('contact.location.label', { ns: 'entities' }),
        accessorKey: 'location',
        accessorFn: (row) => getShortFormattedAddress(row.location),
      },
      {
        header: t('contact.website', { ns: 'entities' }),
        accessorKey: 'website',
      },
      {
        header: t('contact.status', { ns: 'entities' }),
        accessorKey: 'status',
        filterVariant: 'select',
        filterSelectOptions: [
          {
            label: t('status.active', { ns: 'enums' }),
            value: Status.Active,
          },
          {
            label: t('status.disabled', { ns: 'enums' }),
            value: Status.Disabled,
          },
        ],
        Cell: ({ renderedCellValue }) => {
          return capitalize(renderedCellValue);
        },
      },
      {
        header: t('contact.ancestry.label', { ns: 'entities' }),
        accessorKey: 'ancestry',
        accessorFn: (row) => row.ancestry?.propertyGroupName,
      },
    ],
    [],
  );

  // desktop table
  const tableDefaults = useDefaultTableOptions<Contact>(TableType.NESTED);
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: contacts,
    columns,
    enableRowVirtualization: true,
    rowVirtualizerInstanceRef,
    rowVirtualizerOptions: { overscan: 5 },
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      density: 'compact',
      columnVisibility: {
        id: false,
        name: true,
        description: true,
        primaryEmail: true,
        primaryPhone: true,
        secondaryEmail: false,
        secondaryPhone: false,
        tertiaryPhone: false,
        location: false,
        status: false,
        ancestry: false,
        website: false,
      },
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    state: {
      isLoading,
      sorting,
      showLoadingOverlay: false,
    },
    onSortingChange: setSorting,
    getRowId: (row) => `${row.id}`,
    renderTopToolbar: ({ table }) => (
      <MRTableToolbar
        table={table}
        hidden={formMode !== 'none'}
        isSubmitting={fetcher.state === 'submitting'}
        optionsDisabled={!canCreate}
        tableType={TableType.NESTED}
        menuOptions={[
          {
            onClick: () => handleCreateClick(),
            content: t('buttons.create', {
              name: t('contact.label', { ns: 'entities' }),
            }),
            disabled: !canCreate,
            icon: <Add />,
          },
        ]}
      >
        <Stack
          direction={'row'}
          flexGrow={1}
          justifyContent={
            displayMode === 'tenant' ? 'flex-start' : 'space-between'
          }
          alignItems={'center'}
        >
          <Typography variant={'h3'} sx={{ flex: 1 }}>
            {t('contact.label_plural', { ns: 'entities' })}
          </Typography>
          {displayMode !== 'tenant' && (
            <EntityLookup
              entityType={EntityTypes.Contact}
              onSelect={handleAddContact}
              renderOption={(option: Contact) => {
                return (
                  <React.Fragment key={option.id}>
                    <Typography variant={'body2'} noWrap fontWeight={'bold'}>
                      {`${option.name}`}
                    </Typography>
                    <Typography variant={'body2'} noWrap>
                      {`${capitalize(
                        option.description || '',
                      )}  ${formatPhoneNumber(option.primaryPhone || '')}`}
                    </Typography>
                  </React.Fragment>
                );
              }}
              size='small'
              sx={{ width: '30%' }}
            />
          )}
        </Stack>
      </MRTableToolbar>
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => {
      const options = [
        <MenuItem
          divider
          key={0}
          onClick={() => {
            handleMakePrimary(row.original.id);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
          disabled={
            row.original.id === 0 || !canAddRemove || displayMode === 'tenant'
          }
        >
          <ListItemIcon>
            <Person />
          </ListItemIcon>
          {t('buttons.makePrimary')}
        </MenuItem>,
        <MenuItem
          key={1}
          divider
          onClick={() => {
            handleRemoveContact(row.original.id);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
          disabled={
            row.original.id === 0 || !canAddRemove || displayMode === 'tenant'
          }
        >
          <ListItemIcon>
            <Delete />
          </ListItemIcon>
          {t('buttons.remove', {
            name: t('contact.label', { ns: 'entities' }),
          })}
        </MenuItem>,
        <MenuItem
          key={2}
          divider
          onClick={() => {
            handleViewRevisions(row.original.id);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
        >
          <ListItemIcon>
            <History />
          </ListItemIcon>
          {t('buttons.view', { name: t('revision.label_plural') })}
        </MenuItem>,
      ];
      if (AuthService.IsSuperAdmin()) {
        options.push(
          <MenuItem
            key={3}
            onClick={() => {
              handleSoftDelete(row.original);
              closeMenu();
            }}
            sx={{
              m: 0,
              backgroundColor: 'warning.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
            }}
          >
            <ListItemIcon>
              <Warning sx={{ color: 'white' }} />
            </ListItemIcon>
            {t('buttons.delete', {
              name: t('contact.label', { ns: 'entities' }),
            })}
          </MenuItem>,
        );
      }
      return options;
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleContactClick(row.original.id),
    }),
  });

  // form outlet
  if (formMode !== 'none') {
    return (
      <Outlet
        context={
          {
            contact,
            setContact,
            onBack: handleCancelAction,
            formMode,
            displayMode,
            fetcherKey,
            contacts,
            entity,
          } satisfies ContactContext
        }
      />
    );
  }

  if (mobile) {
    // mobile list
    const itemHeight = 70;
    const listColumns: Column[] = columns.map((c) => ({
      name: c.header as string,
      searchKey: c.accessorKey as string,
    }));

    const renderItem = (contact: Contact, index: number): ReactNode => (
      <ListItem
        key={contact.id}
        id={contact.id.toString()}
        onClick={() => handleContactClick(contact.id)}
        divider
        secondaryAction={
          <ListItemMenuButton
            menuOptions={[
              {
                onClick: () => handleMakePrimary(contact.id),
                content: t('buttons.makePrimary'),
                disabled:
                  contact.id === 0 || !canAddRemove || displayMode === 'tenant',
              },
              {
                onClick: () => handleRemoveContact(contact.id),
                content: t('buttons.remove', {
                  name: t('contact.label', { ns: 'entities' }),
                }),
                disabled:
                  contact.id === 0 ||
                  !canAddRemove ||
                  !!contact.ancestry ||
                  displayMode === 'tenant',
              },
              {
                onClick: () => handleViewRevisions(contact.id),
                content: t('buttons.view', {
                  name: t('revision.label_plural'),
                }),
              },
            ]}
          />
        }
        sx={{ height: itemHeight, backgroundColor: 'white' }}
      >
        <ListItemText
          primary={
            index === 0 && displayMode !== 'tenant'
              ? `${contact.name} (primary)`
              : contact.name
          }
          secondary={`${contact.primaryEmail || ''} ${formatPhoneNumber(
            contact.primaryPhone || '',
          )}`}
          primaryTypographyProps={{
            noWrap: true,
            variant: 'subtitle1',
          }}
          secondaryTypographyProps={{
            noWrap: true,
            variant: 'subtitle2',
          }}
        />
      </ListItem>
    );

    return (
      <MobilePanelList<Contact>
        data={contacts}
        columns={listColumns}
        renderItem={renderItem}
        itemHeight={itemHeight}
        title={t('contact.label_plural', { ns: 'entities' })}
        menuOptions={[
          {
            onClick: handleCreateClick,
            content: t('buttons.create', { name: t('contact.label', { ns: 'entities' }) }),
            disabled: !canCreate,
            icon: <Add />,
          },
        ]}
        withSearchBar
        heightOffset={TAB_LAYOUT_HEADER_HEIGHT}
      />
    );
  }

  return (
    <FormFrame loading={false}>
      <MaterialReactTable table={table} />
    </FormFrame>
  );
};

export const getFormRouteForDisplayMode = (
  displayMode: DisplayMode,
): ROUTES => {
  switch (displayMode) {
    case 'property':
      return ROUTES.PROPERTY_CONTACTS_FORM;
    case 'group':
      return ROUTES.PROPERTY_GROUP_CONTACTS_FORM;
    case 'tenant':
      return ROUTES.CONTACTS_FORM;
    default:
      return ROUTES.INDEX;
  }
};

export const getHandlerForDisplayMode = (
  displayMode: DisplayMode,
  formMode: FormMode,
): PeopleTabActionHandler => {
  switch (displayMode) {
    case 'property':
      if (formMode === 'create') {
        return PeopleTabActionHandler.PROPERTY_CONTACT_CREATE;
      } else if (formMode === 'edit') {
        return PeopleTabActionHandler.PROPERTY_CONTACT_UPDATE;
      } else if (formMode === 'none') {
        return PeopleTabActionHandler.PROPERTY_CONTACT_UPSERT;
      } else {
        return PeopleTabActionHandler.UNKNOWN;
      }
    case 'group':
      if (formMode === 'create') {
        return PeopleTabActionHandler.PROPERTY_GROUP_CONTACT_CREATE;
      } else if (formMode === 'edit') {
        return PeopleTabActionHandler.PROPERTY_GROUP_CONTACT_UPDATE;
      } else if (formMode === 'none') {
        return PeopleTabActionHandler.PROPERTY_GROUP_CONTACT_UPSERT;
      } else {
        return PeopleTabActionHandler.UNKNOWN;
      }
    case 'tenant':
      if (formMode === 'create') {
        return PeopleTabActionHandler.TENANT_CONTACT_CREATE;
      } else if (formMode === 'edit') {
        return PeopleTabActionHandler.TENANT_CONTACT_UPDATE;
      } else {
        return PeopleTabActionHandler.UNKNOWN;
      }
    default:
      return PeopleTabActionHandler.UNKNOWN;
  }
};

export * from './ContactForm';
